import { POSTS_TYPE_VALUE_PAGE } from "@raiden/library/constants/posts";
import { frontBaseUri } from "@raiden/library/constants/routers/front";
import { apiGetErrorStatus } from "@raiden/library/helpers/api";
import {
  getNumber,
  useRouterNumber,
} from "@raiden/library/hooks/router/useRouterNumber";
import {
  getString,
  useRouterString,
} from "@raiden/library/hooks/router/useRouterString";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import generatePampaUrl from "@raiden/library/libraries/utils/generatePampaUrl";
import Seo from "../../components/Seo";
import { PageErrorHandler } from "../../containers/PageErrorHandler";
import { PostView } from "../../containers/Posts/Common/View";
import { getIdFromSlug } from "../../helpers/getIdFromSlug";

/**
 * @param {object} params
 * @param {number} params.postId
 * @param {number | null} params.revisionId null if we want the current revision
 * @returns {string}
 */
function getPostUrl({ postId, revisionId }) {
  return generateApiUrl({
    id: "@api.posts.view",
    parameters: { postId },
    query: {
      revision_id: revisionId ?? undefined,
      fields: [
        "post.current_revision",
        "post.categories",
        "post.revision.cover",
        "post.revision.files",
      ],
    },
  });
}

/**
 * Resolves the status code of the page based on the response and error.
 * @param {object} params
 * @param {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [params.response]
 * @param {import("@raiden/library/types/Api/ApiError").ApiError} [params.error]
 * @returns {number | undefined}
 */
function getStatus({ response, error }) {
  if (response) {
    if (response.data.type !== POSTS_TYPE_VALUE_PAGE) {
      return 404;
    }
    return 200;
  }
  if (error) {
    return apiGetErrorStatus({ error: error });
  }
  return undefined;
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [postResponse]
 * @property {number} [serverStatus]
 */
/**
 * @param {Props} props
 */
function Page({ postResponse, serverStatus }) {
  const translate = useTranslate();

  const postSlug = useRouterString("postSlug");
  const postId = getIdFromSlug(postSlug);
  const revisionId = useRouterNumber("revisionId") ?? null;

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post>} */
  const { swrResponse: swrResponsePost } = useApi(
    postId !== undefined
      ? getPostUrl({
          postId,
          revisionId,
        })
      : null,
    {
      swrConfig: {
        fallbackData: postResponse,
        revalidateOnMount: !postResponse,
      },
    },
  );

  const status =
    serverStatus ??
    getStatus({ response: swrResponsePost.data, error: swrResponsePost.error });

  const post = swrResponsePost.data?.data;
  const currentRevision = post?.current_revision;
  const title = translate(currentRevision?.seo_title ?? currentRevision?.title);
  const description = translate(
    currentRevision?.seo_description ?? currentRevision?.summary,
  );
  const cover = currentRevision?.cover;
  const imageUrl = cover
    ? generatePampaUrl({
        image: cover,
        mode: "crop",
        format: "default",
      })
    : undefined;

  return (
    <PageErrorHandler status={status}>
      <Seo
        title={title}
        description={description}
        ogType="article"
        imageUrl={imageUrl}
        links={(locale) =>
          generateFrontPath({
            id: "@front.page",
            parameters: {
              locale,
              postSlug,
            },
          })
        }
        structuredData={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: title,
          image: imageUrl ? [imageUrl] : [],
          datePublished: post?.published_at,
          dateModified: post?.updated_at,
        }}
      />

      <PostView swrResponsePost={swrResponsePost} />
    </PageErrorHandler>
  );
}

export default Page;

/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {InitialProps} */
  const values = {};

  if (typeof window === "undefined") {
    const postSlug = getString(context.query.postSlug);
    const postId = getIdFromSlug(postSlug);
    const revisionId = getNumber(context.query.revisionId) ?? null;

    if (postId !== undefined) {
      let postError = undefined;
      /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post> | undefined} */
      const postResponse = await serverApiFetcher(
        getPostUrl({ postId, revisionId }),
        undefined,
        {
          origin: frontBaseUri.toString(),
          cookies: context.cookies,
        },
      ).catch(
        (
          /** @type {import("@raiden/library/types/Api/ApiError").ApiError} */ error,
        ) => {
          postError = error;
          return undefined;
        },
      );

      values.postResponse = postResponse;

      const serverStatus = getStatus({
        response: postResponse,
        error: postError,
      });

      values.serverStatus = serverStatus;

      if (context.res && serverStatus !== undefined) {
        context.res.statusCode = serverStatus;
      }
    }
  }

  return values;
};
